import React from 'react';
import { Icon } from './ui/Icon.jsx';
import { fmt } from '../lib/utils.js';

// ─── SHARED ORDER MODAL ───────────────────────────────────────────────────────
// Read-only view a manager sees when they open a buyer's shared order link
// (?draft=…). Line images are resolved from the live catalogue by product id;
// quantities and prices come from the snapshot the buyer shared.
export default function SharedOrderModal({ draft, products = [], lang, isMobile, onPlace, onClose }) {
  const ar = lang === 'ar';
  if (!draft) return null;

  const byId = {};
  products.forEach(p => { byId[p.id] = p; });

  const hasPrices = draft.lines.some(l => l.price != null);
  const total = draft.lines.reduce((s, l) => s + (l.price != null ? l.price * l.qty : 0), 0);
  const count = draft.lines.reduce((s, l) => s + l.qty, 0);

  const heading  = ar ? 'طلب مشترك للمراجعة' : 'Shared order for review';
  const subtitle = ar ? 'أرسل إليك أحد المشترين هذا الطلب لمراجعته.' : 'A buyer shared this order with you to review.';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobile ? 0 : 24, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} dir={ar ? 'rtl' : 'ltr'}
        style={{ background: 'var(--sedra-card)', width: '100%', maxWidth: 620, borderRadius: isMobile ? 0 : 14, minHeight: isMobile ? '100vh' : 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: ar ? "'Almarai','Tajawal',sans-serif" : "'Inter','Cairo',sans-serif" }}>
        {/* Header */}
        <div style={{ background: 'var(--sedra-forest)', color: '#fff', padding: '18px 22px', borderRadius: isMobile ? 0 : '14px 14px 0 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="ShoppingCart" size={17} /> {heading}</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>{subtitle}</div>
          </div>
          <button onClick={onClose} aria-label={ar ? 'إغلاق' : 'Close'} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Lines */}
        <div style={{ padding: isMobile ? '12px 14px' : '16px 22px' }}>
          {draft.lines.map((l, idx) => {
            const p = byId[l.id];
            const img = p?.image;
            const name = (ar && (l.name_ar || p?.name_ar)) ? (l.name_ar || p?.name_ar) : (l.name_en || p?.name_en || l.sku);
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--sedra-line)' }}>
                <div style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--sedra-image-well)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sedra-ink-muted)' }}>
                  {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Icon name="Package" size={22} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sedra-ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                  <div style={{ fontSize: 12, color: 'var(--sedra-ink-muted)', marginTop: 2 }}>
                    {l.sku ? l.sku + ' · ' : ''}{ar ? 'الكمية' : 'Qty'} {l.qty}
                    {l.price != null ? ' · ' + fmt(l.price) : ''}
                  </div>
                </div>
                {l.price != null && (
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--sedra-forest)', whiteSpace: 'nowrap' }}>{fmt(l.price * l.qty)}</div>
                )}
              </div>
            );
          })}

          {draft.note ? (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--sedra-sage-wash)', borderRadius: 8, fontSize: 13, color: 'var(--sedra-ink)' }}>
              <span style={{ fontWeight: 700 }}>{ar ? 'ملاحظة المشتري: ' : 'Buyer note: '}</span>{draft.note}
            </div>
          ) : null}

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 14 }}>
            <span style={{ color: 'var(--sedra-ink-muted)' }}>{ar ? `${draft.lines.length} صنف · ${count} قطعة` : `${draft.lines.length} items · ${count} units`}</span>
            {hasPrices && <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--sedra-forest)' }}>{ar ? 'الإجمالي ' : 'Total '}{fmt(total)}</span>}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexDirection: isMobile ? 'column' : 'row' }}>
            <button onClick={onPlace} style={{ flex: 1, padding: '13px 20px', borderRadius: 9, background: 'var(--sedra-pistachio-deep)', color: 'var(--sedra-cream)', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Icon name="ShoppingCart" size={16} /> {ar ? 'إتمام هذا الطلب' : 'Place this order'}
            </button>
            <button onClick={onClose} style={{ flex: isMobile ? 'unset' : '0 0 auto', padding: '13px 20px', borderRadius: 9, background: 'transparent', color: 'var(--sedra-ink-muted)', border: '1px solid var(--sedra-line)', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
              {ar ? 'تصفّح المتجر' : 'Browse catalogue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
